<template>
  <div v-if="showDropdown">
    <v-popover
      interactive
      tag="div"
      content-tag="div"
      :trigger="'click'"
      :role="'poper'"
      class="tab-dropdown"
      :on-show="() => openDropdownOptions"
      :on-hide="handleOnHideDropdown"
      :placement="'bottom-start'"
      :append-to="bodyRef"
    >
      <div
        class="tab-item h-[22px]"
        :class="{ selected: tab.selected }"
      >
        <em
          v-if="tab.icon"
          class="tab-item-icon custom-icons"
          :style="getCustomIconStyle(iconPath(tab.icon))"
        />
        <span class="tab-item-label">
          {{ tab.label }}
        </span>
        <em
          v-if="tab.selected"
          class="custom-icons ml-2"
          :style="getCustomIconStyle(iconPath('chevron-down'))"
        />
        <slot v-if="tab.hasSlot" />
      </div>

      <template #content="{ state, hide }">
        <FilterDropdown
          v-if="state.isVisible"
          class="tab-list-dropdown"
          :title="tab.dropdownOptions.title"
          :placeholder="tab.dropdownOptions.searchPlaceholder"
          :search-results="getDropdownItems(hide)"
          :dropdown-footer="true"
          :footer-text="tab.dropdownOptions.footerText"
          :on-item-click="(template) => handleDropdownItemClick(hide, template)"
          :on-item-right-side-click="(template) => handleDropdownItemClick(hide, template)"
          :selected-item-check="selectedItemCheck"
          :scroll-to-selected="true"
          :hide-filter-popup="() => {}"
          @esc="hide"
          @footerClick="handleFooterClick"
          @query-change="handleQueryChange"
        />
      </template>
    </v-popover>
  </div>
  <div
    v-else
    class="tab-item h-full"
    :class="{ selected: tab.selected }"
  >
    <em
      v-if="tab.icon"
      class="tab-item-icon custom-icons"
      :style="getCustomIconStyle(iconPath(tab.icon))"
    />
    <span class="tab-item-label">
      {{ tab.label }}
    </span>
    <slot v-if="tab.hasSlot" />
  </div>
</template>
<script setup lang="ts">
import { Tab } from '@/types/tabs';
import useCustomIcon from '@/utilities/composables/useCustomIcon';
import { computed, ref } from 'vue';
import constants from '@/utilities/constants';
import { useRouter } from 'vue-router';
import FilterDropdown from '@/components/widgets/filters/FilterDropdown';

const router = useRouter();
const emit = defineEmits(['click-dropdown-item', 'closeDropdown']);

const props = defineProps<{ tab: Tab; dropdownForTab?: string; tabClickCount?: number }>();

const bodyRef = ref(document.body);
const searchQuery = ref('');

const { iconPath, getCustomIconStyle } = useCustomIcon();

const showDropdown = computed(() => {
  return props.dropdownForTab === props.tab.id;
});

const openDropdownOptions = computed(() => {
  return props.tab.selected && (props?.tabClickCount || 0) >= 1;
});

const selectedItemCheck = computed(() => {
  // Prevent showing check icon for selected item that shows when searching
  return searchQuery.value ? -1 : props.tab.dropdownOptions.selectedItem;
});

const handleOnHideDropdown = () => {
  searchQuery.value = '';
};

const handleDropdownItemClick = (hide, template) => {
  if (template) props.tab.dropdownOptions.clickFn(template);

  hide?.();
  emit('click-dropdown-item');
};

const getDropdownItems = (hide) => {
  return props.tab.dropdownOptions.items.reduce((acc, item) => {
    if (item.label.toLowerCase().includes(searchQuery.value.toLowerCase())) {
      acc.push({
        ...item,
        ...(item.additionalRightIconCallback && {
          additionalRightIconCallback: () => {
            item.additionalRightIconCallback();
            handleDropdownItemClick(hide);
          },
        }),
      });
    }
    return acc;
  }, []);
};

const handleFooterClick = ($event) => {
  const { note: noteTemplate } = constants.settingsTemplate;
  router.push({ name: noteTemplate.root.name });
  emit('closeDropdown', $event);
};

const handleQueryChange = (query) => {
  searchQuery.value = query;
};
</script>
<style lang="scss">
.tab-dropdown {
  @apply h-[22px];
  .trigger {
    display: block !important;
  }
}
.tab-item {
  @apply flex items-center justify-center px-2 border-y-0 cursor-pointer bg-standard-bg w-fit overflow-hidden;
  &.selected {
    @apply bg-selected-bg;
  }

  .tab-item-icon {
    @apply flex w-4 h-4 mr-1;
  }

  .tab-item-label {
    @apply text-sm text-text-default truncate;
  }

  .selected-hover {
    &.selected {
      &:hover {
        @apply bg-emphasised-hover;
      }
    }
  }
}

.tab-list-dropdown {
  height: 100%;
  max-height: unset;

  .__view,
  .__hidebar {
    max-height: 350px;
  }

  .item-label {
    @apply font-normal;
  }
}

.tab-dropdown-popover {
  z-index: 9998 !important;

  .wrapper {
    .popover-inner {
      div {
        .dropdown {
          position: unset;
        }
      }
    }
  }
}
</style>
